import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import { useOwnerApi, commercialError } from '../commercial/useCommercialApi';
import { usd, usdAnnual, limitValue, primaryLabel, listFrom } from '../commercial/commercialFormat';
import PlanVersionEditor from './PlanVersionEditor';
import CommercialCatalogGovernance from './CommercialCatalogGovernance';
import RateCardManagement from './RateCardManagement';
import AddonPackAuthoring from './AddonPackAuthoring';
import CommercialReviewRequests from './CommercialReviewRequests';
import { RefreshCw, AlertTriangle, Package, Layers, Boxes, Plug, CheckCircle2, Coins, Gavel, Inbox } from 'lucide-react';

/**
 * SACP Increment 3 — actionable SuperAdmin Commercial Control Tower.
 * Wired to the LIVE owner commercial APIs (/api/v1/super-admin/commercial/*). Every control
 * calls a real, authorized backend endpoint; mutations require a reason and show the audit result.
 * Authorization is enforced by the backend OTP-session dependency, not by this component.
 */
const RELEASE_STATES = [
  'draft', 'planned', 'in_development', 'internal_testing', 'private_beta',
  'limited_release', 'generally_available', 'deprecated', 'retired',
];

const CommercialControlTower = () => {
  const { superAdmin } = useSuperAdminAuth();
  const api = useOwnerApi(superAdmin);

  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [featureQuery, setFeatureQuery] = useState('');   // Feature Catalog search (key / name / category / release)
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [editorPlan, setEditorPlan] = useState(null);
  // Governance secondary navigation (single primary "Governance" tab; two governed views, both discoverable).
  const [govView, setGovView] = useState('catalog');   // 'catalog' | 'ratecard'
  // "Sync feature catalogue" — owner action that upserts feature/plan DEFINITIONS from source into the
  // catalogue (idempotent). It NEVER changes tenant grants/overrides, plan assignments or subscriptions.
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncErr, setSyncErr] = useState(null);

  // GOV13-A4 — Add Plan (create a new plan header + initial DRAFT version via POST /commercial/plans).
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addErr, setAddErr] = useState(null);
  const emptyAdd = { display_name: '', plan_key: '', public_description: '', monthly_price_cents: '', annual_price_cents: '', currency: 'USD', trial_days: '', billing_class: 'standard' };
  const [addForm, setAddForm] = useState(emptyAdd);

  const addPlan = async () => {
    if (addBusy) return;
    if (!addForm.display_name.trim()) { setAddErr('Plan name is required.'); return; }
    setAddBusy(true); setAddErr(null);
    try {
      const body = {
        display_name: addForm.display_name.trim(),
        plan_key: addForm.plan_key.trim() || addForm.display_name.trim(),
        public_description: addForm.public_description.trim(),
        billing_class: addForm.billing_class,
        currency: (addForm.currency || 'USD').toUpperCase(),
        monthly_price_cents: addForm.monthly_price_cents !== '' ? parseInt(addForm.monthly_price_cents, 10) : null,
        // Truth: annual is null unless an intentional POSITIVE amount is entered — a 0/blank is never "$0/yr".
        annual_price_cents: (addForm.annual_price_cents !== '' && parseInt(addForm.annual_price_cents, 10) > 0) ? parseInt(addForm.annual_price_cents, 10) : null,
        trial_days: addForm.trial_days !== '' ? parseInt(addForm.trial_days, 10) : null,
      };
      await api.post('/api/v1/super-admin/commercial/plans', body);
      setAddOpen(false); setAddForm(emptyAdd);
      setToast({ ok: true, msg: `Plan "${body.display_name}" created as a DRAFT. Compose its version, then publish to make it live. It is not public or tenant-assignable until published.` });
      await load();
    } catch (e) {
      setAddErr(commercialError(e));
    } finally {
      setAddBusy(false);
    }
  };

  const runCatalogueSync = async () => {
    if (syncBusy) return;
    setSyncBusy(true); setSyncErr(null); setSyncResult(null);
    try {
      // Shared authenticated owner client (Bearer handled inside useOwnerApi — no token/localStorage here).
      const r = await api.post('/api/v1/super-admin/commercial/seed', {});
      setSyncResult(r.data || { seeded: true });
      await load();                       // refresh the catalogue so new rows (e.g. MFA) appear
      setToast({ ok: true, msg: 'Feature catalogue synchronised. Tenant grants were not changed.' });
    } catch (e) {
      setSyncErr(commercialError(e));     // safe message only — never a raw response / stack
    } finally {
      setSyncBusy(false);
    }
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      // Add-ons & Packs authoring is delegated to <AddonPackAuthoring/>, which owns its own governed
      // read+write catalogue calls. This loader only feeds the Plans/Feature/Integration tabs.
      // Request the FULL catalogue, not just the first paginated page — the backend defaults to
      // page_size=50 and there are >50 features, so omitting this silently truncated the alphabetical
      // tail (e.g. tracker_management / tracking_sms / vehicle_management) from the live view.
      const [p, f, i] = await Promise.allSettled([
        api.get('/api/v1/super-admin/commercial/plans'),
        api.get('/api/v1/super-admin/commercial/features', { params: { page_size: 1000 } }),
        api.get('/api/v1/super-admin/commercial/integrations', { params: { page_size: 1000 } }),
      ]);
      if ([p, f, i].every((r) => r.status === 'rejected')) throw p.reason || new Error('load failed');
      // features/integrations come back through paginate(): { items: [...] }. Read `.items`.
      const planList = p.status === 'fulfilled' ? listFrom(p.value.data) : [];
      setFeatures(f.status === 'fulfilled' ? listFrom(f.value.data) : []);
      setIntegrations(i.status === 'fulfilled' ? listFrom(i.value.data) : []);
      // Price + limits live on the PUBLISHED VERSION, not the plan catalog doc. Enrich each plan
      // with its active version (real backend values — never hard-coded).
      const enriched = await Promise.all(planList.map(async (pl) => {
        try {
          const d = await api.get(`/api/v1/super-admin/commercial/plans/${encodeURIComponent(pl.id)}`);
          const versions = d.data?.versions || [];
          const active = versions.find((v) => v.id === pl.latest_version_id)
            || versions.find((v) => v.state === 'active') || versions[0] || null;
          return { ...pl, _version: active };
        } catch (_) { return { ...pl, _version: null }; }
      }));
      setPlans(enriched);
    } catch (err) {
      setError(commercialError(err));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  // Release-state changes go through a reason modal (no native prompt). The select only PROPOSES the
  // change; nothing mutates until the reason is supplied and confirmed, so an accidental select reverts.
  const [rsModal, setRsModal] = useState(null);   // { featureKey, featureName, current, next } | null
  const [rsReason, setRsReason] = useState('');
  const [rsBusy, setRsBusy] = useState(false);
  const [rsErr, setRsErr] = useState(null);

  const proposeReleaseState = (feature, next) => {
    if (next === (feature.release_state || 'generally_available')) return;
    setRsModal({ featureKey: feature.feature_key, featureName: feature.display_name || feature.feature_key, current: feature.release_state || 'generally_available', next });
    setRsReason(''); setRsErr(null);
  };

  const submitReleaseState = async () => {
    if (!rsReason.trim()) { setRsErr('A reason is required (recorded in the audit log).'); return; }
    setRsBusy(true); setRsErr(null);
    try {
      const res = await api.post(
        `/api/v1/super-admin/commercial/features/${encodeURIComponent(rsModal.featureKey)}/release-state`,
        { state: rsModal.next, reason: rsReason.trim() },
      );
      setToast({ ok: true, msg: `Release state updated to "${res.data?.state || rsModal.next}". Audit recorded.` });
      setRsModal(null); setRsReason('');
      await load();
    } catch (err) {
      setRsErr(commercialError(err));
    } finally {
      setRsBusy(false);
    }
  };

  const Pill = ({ children, tone = 'gray' }) => {
    const tones = {
      green: 'bg-green-100 text-green-800', gray: 'bg-gray-100 text-gray-600',
      amber: 'bg-amber-100 text-amber-800', purple: 'bg-purple-100 text-purple-800',
      blue: 'bg-blue-100 text-blue-800', red: 'bg-red-100 text-red-800',
    };
    return <Badge className={tones[tone]}>{children}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Commercial Control Tower</h1>
          <p className="text-sm text-gray-500 mt-1">Plans, pricing, credits, rates, packs, modules, integrations and tenant commercial lifecycle. Platform operations &amp; runtime governance live in Control Tower.</p>
          <p className="text-gray-600 mt-1">USD plans, features, integrations, add-ons — live owner governance</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => { setAddErr(null); setAddOpen(true); }} disabled={loading || addBusy}>
            + Add Plan
          </Button>
          <Button onClick={() => { setSyncErr(null); setSyncResult(null); setSyncOpen(true); }} variant="outline" disabled={loading || syncBusy}>
            <RefreshCw className={`h-4 w-4 mr-2 ${syncBusy ? 'animate-spin' : ''}`} /> Sync feature catalogue
          </Button>
          <Button onClick={load} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Confirm modal — synchronises DEFINITIONS only; tenant grants/overrides are never changed. */}
      <Dialog open={syncOpen} onOpenChange={(o) => { if (!syncBusy) setSyncOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sync feature catalogue</DialogTitle>
            <DialogDescription>
              This synchronises feature, plan and add-on <b>definitions</b> from the platform source into the
              catalogue (idempotent). It does <b>not</b> change any tenant’s grants/denies, plan assignment or
              subscription — those stay exactly as they are.
            </DialogDescription>
          </DialogHeader>
          {syncErr && <p className="text-sm text-red-600" role="alert">{syncErr}</p>}
          {syncResult && (
            <p className="text-sm text-green-700" role="status">
              Synchronised.{typeof syncResult.indexed === 'number' ? ` ${syncResult.indexed} indexes ensured.` : ''} You can close this.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setSyncOpen(false)} disabled={syncBusy}>{syncResult ? 'Close' : 'Cancel'}</Button>
            {!syncResult && (
              <Button onClick={runCatalogueSync} disabled={syncBusy}>
                {syncBusy ? 'Syncing…' : 'Confirm sync'}</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* GOV13-A4 — Add Plan: creates a DRAFT plan header + initial version. Not public/assignable until published. */}
      <Dialog open={addOpen} onOpenChange={(o) => { if (!addBusy) setAddOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Plan</DialogTitle>
            <DialogDescription>
              Creates a new plan as a <b>draft</b> with an initial version. It is <b>not</b> shown at signup and
              <b> not</b> assignable to tenants until you compose and <b>publish</b> a version. Prices are in integer
              cents; annual pricing is optional and never derived.
            </DialogDescription>
          </DialogHeader>
          {addErr && <p className="text-sm text-red-600" role="alert">{addErr}</p>}
          <div className="space-y-2">
            <label className="block text-xs text-gray-700">Plan name *
              <input className="mt-1 w-full border rounded px-2 py-1 text-sm" value={addForm.display_name} onChange={(e) => setAddForm({ ...addForm, display_name: e.target.value })} />
            </label>
            <label className="block text-xs text-gray-700">Plan key <span className="text-gray-400">(optional — a safe slug is generated)</span>
              <input className="mt-1 w-full border rounded px-2 py-1 text-sm" value={addForm.plan_key} onChange={(e) => setAddForm({ ...addForm, plan_key: e.target.value })} />
            </label>
            <label className="block text-xs text-gray-700">Public description
              <input className="mt-1 w-full border rounded px-2 py-1 text-sm" value={addForm.public_description} onChange={(e) => setAddForm({ ...addForm, public_description: e.target.value })} />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs text-gray-700">Monthly price (cents)
                <input type="number" className="mt-1 w-full border rounded px-2 py-1 text-sm" value={addForm.monthly_price_cents} onChange={(e) => setAddForm({ ...addForm, monthly_price_cents: e.target.value })} />
              </label>
              <label className="block text-xs text-gray-700">Annual price (cents) <span className="text-gray-400">optional — leave blank if not set</span>
                <input type="number" min="1" placeholder="leave blank" className="mt-1 w-full border rounded px-2 py-1 text-sm" value={addForm.annual_price_cents} onChange={(e) => setAddForm({ ...addForm, annual_price_cents: e.target.value })} />
              </label>
              <label className="block text-xs text-gray-700">Currency
                <select className="mt-1 w-full border rounded px-2 py-1 text-sm" value={addForm.currency} onChange={(e) => setAddForm({ ...addForm, currency: e.target.value })}>
                  <option value="USD">USD</option>
                  <option value="CAD">CAD</option>
                </select>
              </label>
              <label className="block text-xs text-gray-700">Trial days <span className="text-gray-400">optional — 0 = no trial</span>
                <input type="number" min="0" placeholder="0 = no trial" className="mt-1 w-full border rounded px-2 py-1 text-sm" value={addForm.trial_days} onChange={(e) => setAddForm({ ...addForm, trial_days: e.target.value })} />
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addBusy}>Cancel</Button>
            <Button onClick={addPlan} disabled={addBusy}>{addBusy ? 'Creating…' : 'Create draft plan'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {error && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200" role="alert">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <p className="text-sm text-red-700">{error}</p>
          <Button onClick={load} size="sm" variant="outline" className="ml-auto">Retry</Button>
        </div>
      )}
      {toast && (
        <div className={`flex items-center gap-2 p-3 rounded-lg border ${toast.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`} role="status">
          {toast.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          <span className="text-sm">{toast.msg}</span>
          <button className="ml-auto text-xs underline" onClick={() => setToast(null)}>dismiss</button>
        </div>
      )}

      <Tabs defaultValue="plans">
        {/* Five primary tabs (was six). "Catalog Governance" + "Rate Card Lifecycle" now live under one
            discoverable "Governance" tab with a visible sub-nav — no horizontal overflow, no clipped labels.
            flex-wrap/h-auto keep every label visible from 1280–1920px instead of clipping off-screen. */}
        <TabsList className="flex flex-wrap h-auto justify-start gap-1">
          <TabsTrigger value="plans"><Layers className="h-4 w-4 mr-1" />Plans &amp; Pricing</TabsTrigger>
          <TabsTrigger value="features"><Package className="h-4 w-4 mr-1" />Feature Catalog</TabsTrigger>
          <TabsTrigger value="integrations"><Plug className="h-4 w-4 mr-1" />Integration Catalog</TabsTrigger>
          <TabsTrigger value="addons"><Boxes className="h-4 w-4 mr-1" />Add-ons &amp; Packs</TabsTrigger>
          <TabsTrigger value="review"><Inbox className="h-4 w-4 mr-1" />Review Requests</TabsTrigger>
          <TabsTrigger value="governance"><Gavel className="h-4 w-4 mr-1" />Governance</TabsTrigger>
        </TabsList>

        {/* PLANS & PRICING (USD) */}
        <TabsContent value="plans" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3"><CardTitle className="text-lg">USD Plans (live)</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              {loading ? <p className="text-sm text-gray-400">Loading…</p>
                : plans.length === 0 ? <p className="text-sm text-gray-500">No plans configured.</p>
                : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b"><tr>
                      {['Plan', 'Version', 'Monthly', 'Annual', 'Staff', 'Integrations', 'AI Res.', 'Storage', 'Vehicles', 'Trackers', ''].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {plans.map((p) => {
                        const v = p._version || {};
                        const cents = v.price_cents ?? (v.price_usd != null ? v.price_usd * 100 : null);
                        const lim = v.limits || {};
                        const na = 'Not configured';
                        const money = (c) => (p.id === 'enterprise' ? 'Custom' : (c == null ? na : `${usd(c)}/mo`));
                        const moneyY = (c) => (p.id === 'enterprise' ? 'Custom' : (c == null ? na : `${usdAnnual(c)}/yr`));
                        const lv = (val) => (val == null ? na : limitValue(val));
                        return (
                          <tr key={p.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-900">{primaryLabel(p.display_name || p.display, p.id)}</td>
                            <td className="px-3 py-2">{v.version ? `v${String(v.version).replace(/^v/, '')}` : na}</td>
                            <td className="px-3 py-2">{money(cents)}</td>
                            <td className="px-3 py-2">{moneyY(cents)}</td>
                            <td className="px-3 py-2">{lv(lim.max_staff)}</td>
                            <td className="px-3 py-2">{lv(lim.active_integrations)}</td>
                            <td className="px-3 py-2">{lv(lim.monthly_ai_resolutions)}</td>
                            <td className="px-3 py-2">{lim.storage_gb == null ? na : `${limitValue(lim.storage_gb)} GB`}</td>
                            <td className="px-3 py-2">{lv(lim.active_vehicles)}</td>
                            <td className="px-3 py-2">{lv(lim.active_trackers)}</td>
                            <td className="px-3 py-2">
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditorPlan(p.id)}>Manage versions</Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
            </CardContent>
          </Card>
          <p className="text-xs text-gray-400 mt-2">Published plan versions are immutable. Use “Manage versions” to clone a published version to an editable draft, then publish a new immutable version. Existing tenants are never migrated silently.</p>
          {editorPlan && (
            <PlanVersionEditor planId={editorPlan} api={api} onClose={() => setEditorPlan(null)} onChanged={load} />
          )}
        </TabsContent>

        {/* Release-state change modal (replaces native prompt). Reason required; audited server-side. */}
        <Dialog open={!!rsModal} onOpenChange={(o) => { if (!o) { setRsModal(null); setRsReason(''); setRsErr(null); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Change release state</DialogTitle>
              <DialogDescription>
                {rsModal && <><span className="font-medium">{rsModal.featureName}</span>: <span className="font-mono">{rsModal.current}</span> → <span className="font-mono">{rsModal.next}</span></>}
              </DialogDescription>
            </DialogHeader>
            <label className="block text-xs font-semibold text-gray-600 mb-1 mt-2">Reason (recorded in audit) <span className="text-red-500">*</span></label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={rsReason} onChange={(e) => setRsReason(e.target.value)} placeholder="Why is this lifecycle change being made?" />
            {rsErr && <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mt-2"><AlertTriangle className="h-4 w-4" />{rsErr}</div>}
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="ghost" onClick={() => { setRsModal(null); setRsReason(''); setRsErr(null); }} disabled={rsBusy}>Cancel</Button>
              <Button onClick={submitReleaseState} disabled={rsBusy}>{rsBusy ? 'Working…' : 'Apply change'}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* FEATURE CATALOG — with governance axes + actionable release-state */}
        <TabsContent value="features" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <CardTitle className="text-lg">Feature Catalog (live)</CardTitle>
              <input
                type="search"
                aria-label="Search features"
                placeholder="Search feature key, name, category…"
                className="border rounded px-3 py-1.5 text-sm w-full sm:w-72"
                value={featureQuery}
                onChange={(e) => setFeatureQuery(e.target.value)}
              />
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {loading ? <p className="text-sm text-gray-400">Loading…</p>
                : features.length === 0 ? <p className="text-sm text-gray-500">No features configured.</p>
                : (() => {
                  const q = featureQuery.trim().toLowerCase();
                  const visibleFeatures = q
                    ? features.filter((f) => [f.feature_key, f.display_name, f.commercial_category, f.category, f.release_state, f.primary_engine_key]
                        .some((v) => String(v || '').toLowerCase().includes(q)))
                    : features;
                  if (visibleFeatures.length === 0) {
                    return <p className="text-sm text-gray-500">No features match “{featureQuery}”.</p>;
                  }
                  return (
                  <>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b"><tr>
                      {['Feature', 'Category', 'Owning Engine', 'Release State', 'Customer Exposure', 'Config', 'Change Release'].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {visibleFeatures.map((f) => (
                        <tr key={f.feature_key} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <div className="font-medium text-gray-900">{primaryLabel(f.display_name, f.feature_key)}</div>
                            <div className="text-xs text-gray-400 font-mono">{f.feature_key}</div>
                          </td>
                          <td className="px-3 py-2"><Pill tone="blue">{f.commercial_category || f.category || '—'}</Pill></td>
                          {/* D1 read-only: owning engine + tenant scope (never grants access; SACP governs availability). */}
                          <td className="px-3 py-2">
                            <div className="text-xs text-gray-700">{f.primary_engine_key || '—'}</div>
                            {(f.dependency_engine_keys || []).length > 0 && (
                              <div className="text-[10px] text-gray-400">+ {(f.dependency_engine_keys || []).join(', ')}</div>
                            )}
                            {f.tenant_scope === 'all_registered_businesses'
                              ? <div className="text-[10px] text-purple-600">all registered businesses</div>
                              : <div className="text-[10px] text-gray-400">tenant-scoped</div>}
                          </td>
                          <td className="px-3 py-2"><Pill tone={f.release_state === 'generally_available' ? 'green' : f.release_state === 'in_development' ? 'amber' : 'gray'}>{f.release_state || 'generally_available'}</Pill></td>
                          <td className="px-3 py-2">{f.customer_exposure || 'business_only'}</td>
                          <td className="px-3 py-2">{f.configuration_requirement || 'none'}</td>
                          <td className="px-3 py-2">
                            <select
                              aria-label={`Change release state for ${f.display_name || f.feature_key}`}
                              className="border rounded px-2 py-1 text-xs"
                              value={f.release_state || 'generally_available'}
                              onChange={(e) => proposeReleaseState(f, e.target.value)}
                            >
                              {RELEASE_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-gray-400 mt-2">
                    Showing {visibleFeatures.length}{q ? ` of ${features.length}` : ''} feature{visibleFeatures.length === 1 ? '' : 's'}.
                  </p>
                  </>
                  );
                })()}
            </CardContent>
          </Card>
          <p className="text-xs text-gray-400 mt-2">Global safety state (kill switch) overrides commercial entitlement. Changing a release state requires a reason and is audited.</p>
        </TabsContent>

        {/* INTEGRATION CATALOG */}
        <TabsContent value="integrations" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Integration Catalog (live)</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              {loading ? <p className="text-sm text-gray-400">Loading…</p>
                : integrations.length === 0 ? <p className="text-sm text-gray-500">No connectors configured.</p>
                : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b"><tr>
                      {['Connector', 'Provider', 'Pricing Class', 'Slot Consuming', 'Status'].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {integrations.map((it) => (
                        <tr key={it.integration_key} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium text-gray-900">{primaryLabel(it.display_name, it.integration_key)}</td>
                          <td className="px-3 py-2">{it.provider || '—'}</td>
                          <td className="px-3 py-2"><Pill tone="blue">{it.pricing_class || '—'}</Pill></td>
                          <td className="px-3 py-2">{it.counts_toward_active_slots ? 'Yes' : 'No'}</td>
                          <td className="px-3 py-2"><Pill tone={it.status === 'released' ? 'green' : 'gray'}>{it.status || 'released'}</Pill></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </CardContent>
          </Card>
          <p className="text-xs text-gray-400 mt-2">Tenant credentials are never shown here — only safe governance metadata.</p>
        </TabsContent>

        {/* ADD-ONS & PACKS — governed authoring (paid add-ons, credit packs, capacity-limit usage packs,
            + Stripe TEST mapping). Delegated to the dedicated governed authoring component; it owns its own
            catalogue read/write against /super-admin/commercial/* and renders the write-gate state safely. */}
        <TabsContent value="addons" className="pt-4">
          <AddonPackAuthoring />
        </TabsContent>

        {/* GOV13 — Enterprise / on-request COMMERCIAL_REVIEW intake (owner plane only; owner session api). */}
        <TabsContent value="review" className="pt-4">
          <CommercialReviewRequests api={api} />
        </TabsContent>

        {/* GOVERNANCE — one discoverable primary tab hosting both governed views via a visible sub-nav.
            Catalog Governance (catalogue/release/dependency/audit) and Rate Card Lifecycle (rate-card
            versioning/authoring/publishing) are related, not separate systems. Both existing components
            (CommercialCatalogGovernance, RateCardManagement) are reused unchanged — no duplicate authority. */}
        <TabsContent value="governance" className="pt-4">
          <div className="flex flex-wrap items-center gap-2 mb-4 border-b pb-3" role="tablist" aria-label="Governance views">
            <button
              type="button"
              role="tab"
              aria-selected={govView === 'catalog'}
              onClick={() => setGovView('catalog')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border ${govView === 'catalog' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            >
              <Coins className="h-4 w-4 mr-1 inline" />Catalog Governance
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={govView === 'ratecard'}
              onClick={() => setGovView('ratecard')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border ${govView === 'ratecard' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            >
              <Coins className="h-4 w-4 mr-1 inline" />Rate Card Lifecycle
            </button>
            <span className="text-xs text-gray-400 ml-1">
              Catalog Governance = catalogue / release / dependency view · Rate Card Lifecycle = rate-card versioning &amp; publishing
            </span>
          </div>

          {govView === 'catalog' ? (
            <div>
              {/* D18-C — catalog governance: rate card, credit packs, transaction fees, tenant impact, audit */}
              <div className="flex justify-end mb-3">
                <Button size="sm" variant="outline" onClick={() => setGovView('ratecard')}>
                  <Coins className="h-4 w-4 mr-1" />Manage Rate Card
                </Button>
              </div>
              <CommercialCatalogGovernance api={api} />
            </div>
          ) : (
            <div>
              {/* GOV13-A5.3 — governed activity lifecycle + rate-card versioning + activation readiness */}
              <div className="flex justify-start mb-3">
                <Button size="sm" variant="outline" onClick={() => setGovView('catalog')}>
                  ← Back to Catalog Governance
                </Button>
              </div>
              <RateCardManagement />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CommercialControlTower;
